import { zodResolver } from '@hookform/resolvers/zod';
import type { CustomFieldValueWithDefinition } from '@vy/custom-fields-types';
import type { Contact, Customer, UpdateCustomer } from '@vy/types';
import {
  isEmptyArray,
  isEmptyObject,
  isEmptyString,
  isError,
  isNotEmptyString,
  isNotNullOrUndefined,
} from '@vy/utils';
import { isNull } from 'lodash';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useUpdateCustomer } from '../../../../hooks/requests/use-update-customer';
import { getDirtyValues } from '../../../../services/form-utils';
import { queryKeys } from '../../../../services/query-key/query-keys';
import { runMutation } from '../../../../utils/vy-mutation';
import { userMessage } from '../../../../utils/vy-toasts';
import { isUsableCustomFieldValue } from '../../../../components/custom-fields/is-usable-custom-field-value';
import { UpdateCustomerWithExtendedContactsFieldSchema } from './types/update-customer-with-extended-contacts';

function toFormCustomFieldValues(items: CustomFieldValueWithDefinition[]) {
  return items.map(({ customFieldDefinitionId, fieldName, value }) => ({
    customFieldDefinitionId,
    fieldName,
    value,
  }));
}

export const useUpdateCustomerForm = (
  customerId: string,
  customer?: Customer,
  children?: Customer[],
  customFieldsWithDefinitions?: CustomFieldValueWithDefinition[],
) => {
  const updateCustomerMutation = useUpdateCustomer(customerId);
  const queryClient = useQueryClient();

  const methods = useForm<UpdateCustomer>({
    resolver: zodResolver(UpdateCustomerWithExtendedContactsFieldSchema),
    defaultValues: {
      ...customer,
    },
  });

  useEffect(() => {
    if (isNotNullOrUndefined(customer)) {
      const customerContacts = isNotNullOrUndefined(customer.contacts) ? customer.contacts : [];
      const existedCustomerContacts = customerContacts.map((contact) => ({
        ...contact,
        isFromDb: true,
      }));
      const isNoContacts = isEmptyArray(existedCustomerContacts);

      methods.reset({
        ...customer,
        contacts: [
          ...existedCustomerContacts,
          ...(isNoContacts
            ? [{ email: '', name: '', title: '', phone: '', receiveInvoiceEmail: true, isFromDb: false }]
            : []),
        ],
        customFieldValues: toFormCustomFieldValues(customFieldsWithDefinitions ?? []),
      });
    }
  }, [customer, children, customFieldsWithDefinitions, methods]);

  const handleMutationSuccess = async () => {
    await queryClient.invalidateQueries(queryKeys.customer(customerId));
    methods.reset(methods.getValues());
  };

  const validateForm = async () => {
    const crmId = methods.getValues('crmMetadata.crmId');
    const erpId = methods.getValues('erpMetadata.erpId');

    if (isEmptyString(erpId)) {
      methods.setValue('erpMetadata', undefined);
    }

    if (isEmptyString(crmId)) {
      methods.setValue('crmMetadata', undefined);
    }

    const isValid = await methods.trigger();

    if (!isValid) {
      userMessage('error', 'Please fix the errors before saving');

      return false;
    }

    return true;
  };

  const getUpdatedFields = () => {
    const data = methods.getValues();
    const dirtyFields = methods.formState.dirtyFields;

    const originalContacts = customer?.contacts ?? [];
    const upcomingContacts = data.contacts ?? [];
    const nonEmptyUpcomingContacts = upcomingContacts.filter((contact) => isNotEmptyString(contact.email));
    const contactsAmountChanged = originalContacts.length !== nonEmptyUpcomingContacts.length;

    const originalAliases = customer?.aliases ?? [];
    const upcomingAliases = data.aliases ?? [];
    const aliasesAmountChanged = originalAliases.length !== upcomingAliases.length;

    let updatedFields: Partial<UpdateCustomer> | null = null;

    if (!isEmptyObject(dirtyFields)) {
      updatedFields = getDirtyValues(dirtyFields, data);
    }

    if (contactsAmountChanged) {
      updatedFields = {
        ...updatedFields,
        contacts: upcomingContacts,
      };
    }

    if (aliasesAmountChanged) {
      updatedFields = {
        ...updatedFields,
        aliases: upcomingAliases,
      };
    }

    if (isNull(updatedFields)) {
      return null;
    }

    const aliasesToSend = isNotNullOrUndefined(updatedFields?.aliases) ? { aliases: data.aliases } : {};

    return {
      ...updatedFields,
      ...aliasesToSend,
    };
  };

  const onSubmit = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    const updatedFields = getUpdatedFields();

    if (!updatedFields) {
      return;
    }

    const {
      contacts: updatedContacts,
      customFieldValues: updatedCustomFieldValues,
      ...restUpdatedFields
    } = updatedFields;

    const preparedContactsToUpdate = updatedContacts?.reduce<Contact[]>((result, contact) => {
      if (isNotEmptyString(contact.email)) {
        result.push({
          email: contact.email,
          name: contact.name,
          title: contact.title,
          phone: contact.phone,
          receiveInvoiceEmail: contact.receiveInvoiceEmail,
        });
      }

      return result;
    }, []);

    const preparedCustomFieldValues = updatedCustomFieldValues?.filter(isUsableCustomFieldValue);

    await runMutation(
      updateCustomerMutation,
      {
        ...restUpdatedFields,
        contacts: preparedContactsToUpdate,
        customFieldValues: preparedCustomFieldValues,
      },
      {
        pending: 'Saving customer...',
        success: 'Customer saved',
        error: 'Failed to save customer',
      },
      {
        onSuccess: handleMutationSuccess,
        onError: (error) => {
          if (isError(error)) {
            userMessage('error', error.message);
          }
        },
      },
    );
  };

  return { methods, onSubmit };
};
